import React, { useState, useEffect } from "react";

import {
  format,
  addMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
} from "date-fns";
import { BsSearch } from "react-icons/bs";
import Config from "../utils/config";
import AgentDetailsModal from "../components/AgentDetailsModal";
import Spinner from "../components/Spinner";
import axios from "../utils/axios";
import { ToastContainer, toast } from "react-toastify";

const AdminAvailability = () => {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(
    format(new Date(), "yyyy-MM")
  );

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchAgents();
  }, [selectedMonth]);

  useEffect(() => {
    filterAgents();
  }, [startDate, endDate, agents, searchQuery]);

  useEffect(() => {
    const monthStart = startOfMonth(new Date(selectedMonth));
    const monthEnd = endOfMonth(new Date(selectedMonth));
    setStartDate(format(monthStart, "yyyy-MM-dd"));
    setEndDate(format(monthEnd, "yyyy-MM-dd"));
  }, [selectedMonth]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Config.baseURL}/get-all-available-agent/${selectedMonth}`
      );
      console.log(response.data);
      setAgents(response.data);
    } catch (error) {
      console.error(
        "Une erreur s'est produite lors de la récupération des agents !",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const filterAgents = () => {
    let filtered = agents.filter((agent) => agent?.agent != null);

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      filtered = filtered.filter((agent) => {
        const availability = agent.availability ?? [];
        return availability.some((avail) => {
          const availDate = new Date(avail.date);
          return (
            isSameDay(availDate, start) ||
            (availDate >= start && availDate <= end)
          );
        });
      });
    }

    if (searchQuery) {
      const normalizedQuery = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (agent) =>
          agent.agent?.firstName?.toLowerCase().includes(normalizedQuery) ||
          agent.agent?.lastName?.toLowerCase().includes(normalizedQuery) ||
          agent.agent?.email?.toLowerCase().includes(normalizedQuery)
      );
    }

    setFilteredAgents(filtered);
  };

  const handleAgentClick = async (agentId) => {
    try {
      const response = await axios.get(`${Config.baseURL}/agent/${agentId}`);
      console.log(response.data);
      setSelectedAgent(response.data);
      setModalIsOpen(true);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des détails de l'agent :",
        error
      );
    }
  };

  const handleStatusChange = async (agentId, newStatus) => {
    try {
      const response = await axios.patch(
        `${Config.baseURL}/dispo-status/${agentId}`,
        { status: newStatus }
      );

      // Update agents state with the new status
      setAgents((prevAgents) =>
        prevAgents.map((agent) =>
          agent._id === agentId ? { ...agent, status: newStatus } : agent
        )
      );
      toast.success("Statut mis à jour avec succès !");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };



  if (loading) {
    return <Spinner />;
  }

  const renderAgentTable = () => {
    return (
      <div className="w-full min-w-0 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full min-w-[640px] table-auto border-collapse">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr className="text-left">
                <th className="px-4 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap border-b border-gray-200">
                  Prénom
                </th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap border-b border-gray-200">
                  Nom
                </th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap border-b border-gray-200">
                  Email
                </th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap border-b border-gray-200">
                  Poste
                </th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap border-b border-gray-200">
                  Disponibilités
                </th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap border-b border-gray-200">
                  Status
                </th>
                <th className="px-4 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap border-b border-gray-200 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
            {filteredAgents
              .filter((agent) => agent?.agent != null)
              .map((agent, idx) => (
              <tr key={agent._id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/50 hover:bg-gray-50"}>
                <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">
                  {agent.agent?.firstName ?? "—"}
                </td>
                <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">
                  {agent.agent?.lastName ?? "—"}
                </td>
                <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap max-w-[200px] truncate" title={agent.agent?.email}>
                  {agent.agent?.email ?? "—"}
                </td>
                <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">
                  {agent.agent?.poste ?? "—"}
                </td>
                <td className="px-4 py-3 text-sm text-gray-800">
                  <div className="flex flex-wrap gap-1 max-w-[200px]">
                    {(agent.availability ?? []).map((avail, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold text-slate-800"
                        style={{
                          background:
                            avail.day && avail.night
                              ? "linear-gradient(135deg, #FAB615 50%, #9ca3af 50%)"
                              : avail.day
                                ? "#FAB615"
                                : avail.night
                                  ? "#B0B0B0"
                                  : "#e5e7eb",
                        }}
                        title={format(new Date(avail.date), "dd MMM yyyy")}
                      >
                        {format(new Date(avail.date), "dd")}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm whitespace-nowrap">
                  <select
                    value={agent.status}
                    onChange={(e) => handleStatusChange(agent._id, e.target.value)}
                    className={`text-sm px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 ${agent.status === "traité" ? "text-green-700 border-green-200 bg-green-50" : "text-red-600 border-red-200 bg-red-50"
                      }`}
                  >
                    <option
                      value="en cours"
                      className="text-red-600 bg-red-200"
                    >
                      Pas Planifié
                    </option>
                    <option
                      value="traité"
                      className="text-green-600 bg-green-200"
                    >
                      Planifié
                    </option>
                  </select>
                </td>


                <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                  <button
                    className="text-indigo-600 hover:text-indigo-800 font-medium"
                    onClick={() => handleAgentClick(agent._id)}
                  >
                    Voir plus
                  </button>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const formatMonthInFrench = (date) => {
    const options = { year: "numeric", month: "long" };
    return date.toLocaleDateString("fr-FR", options);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6 overflow-x-hidden w-full min-w-0">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">
            Disponibilité des Agents
          </h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="inline-block w-3.5 h-3.5 rounded-full bg-[#FAB615]" />
              <span>Jour</span>
              <span className="inline-block w-3.5 h-3.5 rounded-full bg-[#B0B0B0] ml-2" />
              <span>Nuit</span>
            </div>
            <div className="relative">
              <BsSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher nom, email..."
                className="pl-9 pr-4 py-2.5 sm:py-2 w-full sm:w-56 sm:w-64 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[44px] sm:min-h-0"
              />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-1">
                Mois
              </label>
              <input
                type="month"
                id="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[44px] sm:min-h-0 w-full sm:w-auto"
              />
            </div>
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Date de début
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[44px] sm:min-h-0 w-full sm:w-auto"
              />
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[44px] sm:min-h-0 w-full sm:w-auto"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {renderAgentTable()}
      </div>

      {modalIsOpen && (
        <AgentDetailsModal
          isOpen={modalIsOpen}
          closeModal={() => setModalIsOpen(false)}
          agent={selectedAgent}
          selectedMonth={selectedMonth}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default AdminAvailability;
